import React, { useCallback } from 'react';
import {
  fetchLiveAgentToken,
  LiveAgentQueue,
  type ILogger,
  type TokenListItem,
} from '@bingads-webui-react/msa-copilot-common';
import { useAdsCopilotPilotMap } from '@bingads-webui/ads-copilot-ui';

import { getLiveAgentQueueList } from '../helpers/live-agent-queue-utils';

export type UseGetLiveAgentParams = {
  accountId?: string,
  allowFetchTokenViaApi?: boolean,
  cachedTokensRef?: React.MutableRefObject<TokenListItem[]>,
  locale: string,
  logger?: ILogger,
};

export function useGetLiveAgentToken({
  accountId,
  allowFetchTokenViaApi = false,
  cachedTokensRef,
  locale,
  logger,
}: UseGetLiveAgentParams) {
  const { isIntelligentRoutingEnabled } = useAdsCopilotPilotMap();

  const getLiveAgentToken = useCallback<(queue?: LiveAgentQueue) => Promise<string | null>>((queue = LiveAgentQueue.General) => {
    const tokenFromCache = cachedTokensRef?.current?.find((token) => token.queue === queue)?.token;
    if (tokenFromCache) {
      logger?.trace(`Copilot - Live Agent: Connecting with cached token for queue=${queue}`);
      return Promise.resolve(tokenFromCache);
    }

    if (!allowFetchTokenViaApi) {
      logger?.trace(`Copilot - Live Agent: Could not find cached token for queue=${queue}, returning null`);
      return Promise.resolve(null);
    }

    const supportedQueues = getLiveAgentQueueList(locale, isIntelligentRoutingEnabled);
    const queueToFetch = supportedQueues?.some((q) => q.queue === queue) ? queue : LiveAgentQueue.General;

    logger?.trace(`Copilot - Live Agent: Fetching token from API for queue=${queue}, queueToFetch=${queueToFetch}`);
    return fetchLiveAgentToken<LiveAgentQueue>({
      accountId,
      locale,
      queue: queueToFetch,
      logger,
    });
  }, [
    accountId,
    allowFetchTokenViaApi,
    cachedTokensRef,
    isIntelligentRoutingEnabled,
    locale,
    logger,
  ]);

  return getLiveAgentToken;
}
